// src/components/admin/common/LockInfoModal.tsx

import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { formatAdminDate } from "../../../utils/adminUtils";
import "./LockInfoModal.css";

interface LockInfoModalProps {
  lockedByUsername?: string | null;
  lockedAt?: string | null;
  lockReason?: string | null;
  lockReasonVisible?: boolean;
  onClose: () => void;
}

/**
 * Read-only modal showing the full detail behind a lock: who locked it, when,
 * the admin's note (if any), and whether that note is shown to regular users.
 * Opened from a small info button next to the lock icon in the admin tables
 * for custom topics, matches, clubs, people, and teams — the lock icon itself
 * only shows a truncated/no hint of the reason, so this is the one place an
 * admin can see the complete picture (e.g. a different admin who didn't set
 * the lock themselves).
 */
export const LockInfoModal: React.FC<LockInfoModalProps> = ({
  lockedByUsername,
  lockedAt,
  lockReason,
  lockReasonVisible,
  onClose,
}) => {
  const { t } = useTranslation();

  return ReactDOM.createPortal(
    <div className="lock-info-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lock-info-modal" role="dialog" aria-modal="true">
        <h3 className="lock-info-modal__title">{t("admin_lock_info_title")}</h3>

        <dl className="lock-info-modal__fields">
          <dt>{t("admin_lock_info_locked_by")}</dt>
          <dd>{lockedByUsername || t("admin_lock_info_unknown")}</dd>

          <dt>{t("admin_lock_info_locked_at")}</dt>
          <dd>{lockedAt ? formatAdminDate(lockedAt) : t("admin_lock_info_unknown")}</dd>

          <dt>{t("admin_lock_info_visible_to_users")}</dt>
          <dd>{lockReasonVisible ? t("admin_yes_toggle") : t("admin_no_toggle")}</dd>

          <dt>{t("admin_lock_info_reason")}</dt>
          <dd className="lock-info-modal__reason">
            {lockReason || t("admin_lock_info_no_reason")}
          </dd>
        </dl>

        <div className="lock-info-modal__actions">
          <button className="lock-info-modal__btn" onClick={onClose} autoFocus>
            {t("admin_close")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};